import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import {
    FormComponent,
    IFormDialogData,
} from 'src/app/shared/form/form.component';
import {
    HierarchicalSelectionFormField,
    NumberFormField,
    RadioFormField,
    SingleSelectionFormField,
    FormField,
} from 'src/app/shared/form/formField.model';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ILookup } from 'src/app/shared/form/lookup.model';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { OrgLevelTypeEnum } from 'src/app/enums/orgLevelType.enum';
import { toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { SegSamplingPlan } from 'src/app/models/segSamplingPlan.model';
import { SegSamplingPlanService } from 'src/app/services/segSamplingPlan.service';
import { SegService } from 'src/app/services/seg.service';
import { AgentService } from 'src/app/services/agent.service';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';

const SCOPE_PROPERTY = 'scope';

@Component({
  standalone: false,
    selector: 'app-seg-sampling-plan-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './../../shared/form/form.component.scss',
        './seg-sampling-plan-dialog.component.scss',
    ],
})
export class SegSamplingPlanDialogComponent
    extends FormComponent<SegSamplingPlan>
    implements OnInit
{
    segs: ILookup[] = [];
    agents: ILookup[] = [];
    agentGroups: ILookup[] = [];
    organizationLevels: OrganizationLevel[] = [];
    useAgentGroup = false;

    constructor(
        dialogRef: MatDialogRef<SegSamplingPlanDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        segSamplingPlanService: SegSamplingPlanService,
        private segService: SegService,
        private agentService: AgentService,
        private agentGroupService: AgentGroupService,
        private organizationLevelService: OrganizationLevelService,
        protected injector: Injector,
    ) {
        super(dialogRef, segSamplingPlanService, data, 'Sampling Plan', injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        const orgPayload = JSON.stringify({
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal,
                Field: nameOf<OrganizationLevel>(x => x.orgLevelType),
                Value: `${OrgLevelTypeEnum.Site},${OrgLevelTypeEnum.Function},${OrgLevelTypeEnum.BusinessUnit},${OrgLevelTypeEnum.Corporate}`,
            }),
        });
        forkJoin({
            segs: this.segService.getAll(),
            agents: this.agentService.getAll(),
            agentGroups: this.agentGroupService.getAll(),
            organizationLevels: this.organizationLevelService.getAll(orgPayload),
        }).subscribe({
            next: (result) => {
                this.segs = result.segs;
                this.agents = result.agents;
                this.agentGroups = result.agentGroups;
                this.organizationLevels = result.organizationLevels;
                this.loadEntity();
            },
            error: (error) => this.handleLoadError(error),
        });
    }

    getNewObject(): SegSamplingPlan {
        return {
            segId: null,
            agentId: null,
            agentGroupId: null,
            year: new Date().getFullYear(),
            plannedNumberOfSamples: null,
            organizationLevelId: null,
        };
    }

    validate(): boolean {
        // Mirror the payload onBeforeSave() will post (it nulls the hidden-scope field),
        // then enforce the mutually-exclusive Agent XOR Agent Group constraint client-side.
        const agentId = this.useAgentGroup ? null : this.entity.agentId;
        const agentGroupId = this.useAgentGroup ? this.entity.agentGroupId : null;
        if ((agentId != null) === (agentGroupId != null)) {
            this.toastService.showError(
                this.translateService.getDynamicTranslation(
                    'Exactly one of Agent or Agent Group must be selected',
                ),
            );
            return false;
        }
        return true;
    }

    onBeforeSave(model: SegSamplingPlan): void {
        if (this.useAgentGroup) {
            model.agentId = null;
        } else {
            model.agentGroupId = null;
        }
        // `scope` is a UI-only radio prop bound onto the entity for the form; strip it so it
        // isn't serialized to the BE (BE ignores it, but keep the payload clean).
        delete (model as unknown as Record<string, unknown>)[SCOPE_PROPERTY];
    }

    private loadEntity(): void {
        this.getEntity().subscribe({
            next: (result) => {
                this.entity = result;
                this.initModel();
                this.initFields();
                this.applyScopeState();
            },
            error: (error) => this.handleLoadError(error),
            complete: () => (this.isFormLoading = false),
        });
    }

    private initModel(): void {
        this.useAgentGroup = !!this.entity.agentGroupId;
        (this.entity as unknown as Record<string, unknown>)[SCOPE_PROPERTY] =
            this.useAgentGroup ? 1 : 0;
    }

    private initFields(): void {
        const currentYear = new Date().getFullYear();
        const scopeOptions: ILookup[] = [
            { id: 0, name: this.translateService.getDynamicTranslation('Agent') },
            { id: 1, name: this.translateService.getDynamicTranslation('Agent Group') },
        ];

        this.fields = [
            new HierarchicalSelectionFormField({
                property: nameOf<SegSamplingPlan>(x => x.organizationLevelId),
                name: 'Site',
                required: true,
                options: toOrganizationTree(this.organizationLevels),
                columnSpan: new ColumnSpan(1, 2),
            }),
            new SingleSelectionFormField({
                property: nameOf<SegSamplingPlan>(x => x.segId),
                name: 'SEG',
                required: true,
                options: this.segs,
                searchKeys: [nameOf<ILookup>(x => x.name)],
                columnSpan: new ColumnSpan(1, 2),
            }),
            new RadioFormField({
                property: SCOPE_PROPERTY,
                name: 'Scope',
                required: false,
                options: scopeOptions,
                columnSpan: new ColumnSpan(1, 1),
                onChange: (value: number) => this.onScopeChange(value),
            }),
            new SingleSelectionFormField({
                isVisible: () => this.useAgentGroup === false,
                property: nameOf<SegSamplingPlan>(x => x.agentId),
                name: 'Agent',
                required: true,
                options: this.agents,
                searchKeys: [nameOf<ILookup>(x => x.name)],
                columnSpan: new ColumnSpan(1, 2),
            }),
            new SingleSelectionFormField({
                isVisible: () => this.useAgentGroup === true,
                property: nameOf<SegSamplingPlan>(x => x.agentGroupId),
                name: 'Agent Group',
                required: true,
                options: this.agentGroups,
                searchKeys: [nameOf<ILookup>(x => x.name)],
                columnSpan: new ColumnSpan(1, 2),
            }),
            new NumberFormField({
                property: nameOf<SegSamplingPlan>(x => x.year),
                name: 'Plan Year',
                required: true,
                // DASH-2676: a plan Year must be exactly 4 digits and the current year or later (a sampling plan
                // is forward-looking). min/max drive Material's error state; the validator supplies the specific
                // inline message and blocks submission. There is no upper bound on how far ahead a plan may be.
                min: currentYear,
                max: 9999,
                validator: (value: unknown) => this.validateYear(value),
                columnSpan: new ColumnSpan(1, 2),
            }),
            new NumberFormField({
                property: nameOf<SegSamplingPlan>(x => x.plannedNumberOfSamples),
                name: 'Planned # of Samples',
                required: true,
                min: 1,
                columnSpan: new ColumnSpan(1, 2),
            }),
        ];
    }

    private onScopeChange(value: number): void {
        this.useAgentGroup = Boolean(value);
        this.applyScopeState();
    }

    private applyScopeState(): void {
        const agentField = this.findField(nameOf<SegSamplingPlan>(x => x.agentId));
        const groupField = this.findField(nameOf<SegSamplingPlan>(x => x.agentGroupId));
        if (agentField) agentField.required = !this.useAgentGroup;
        if (groupField) groupField.required = this.useAgentGroup;
        this.updateFieldVisibility();
    }

    private findField(property: string): FormField | undefined {
        return this.fields?.find(f => f.property === property);
    }

    // DASH-2676: enforce the two Year rules. Returns the inline error message, or null when valid. Empty is left
    // to the `required` check so we don't show a value error before the user has typed anything. Mirrors the BE
    // SegSamplingPlanService.ValidateYear guard exactly.
    private validateYear(value: unknown): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const year = Number(value);
        if (!Number.isInteger(year) || year < 1000 || year > 9999) {
            return 'Year must be a 4-digit value';
        }
        if (year < new Date().getFullYear()) {
            return 'Year cannot be in the past';
        }
        return null;
    }

    private handleLoadError(error: unknown): void {
        this.toastService.showError(error);
        this.isFormLoading = false;
    }
}
